/**
 * Geo Manager - Utility functions for geolocation and country detection
 * Provides global functions for detecting user location and managing country switching
 */

// Global variable to cache detected country code
let countryCode = null;

/**
 * Get user's location from Shopify's browsing context API
 * @returns {Promise<string>} - Country name
 */
async function getUserLocation() {
  try {
    const response = await fetch(
      window.Shopify.routes.root +
        'browsing_context_suggestions.json' +
        '?country[enabled]=true' +
        `&country[exclude]=${window.Shopify.country}` +
        '&language[enabled]=true' +
        `&language[exclude]=${window.Shopify.language}`
    );

    const data = await response.json();
    countryCode = data?.detected_values?.country?.handle;
    return data?.detected_values?.country?.name;
  } catch (error) {
    console.error('Error fetching user location:', error);
    return null;
  }
}

/**
 * Get the detected country code (cached or fetch new)
 * @returns {Promise<string>} - ISO country code (defaults to 'US' if unavailable)
 */
async function getCountryCode() {
  if (!countryCode) {
    await getUserLocation();
  }
  return countryCode ?? 'US';
}

/**
 * Get localization data (wait for it if not ready)
 * @returns {Promise<Object>} - Localization data object
 */
function getLocalizationData() {
  return new Promise((resolve) => {
    if (window.localizationData) {
      resolve(window.localizationData);
      return;
    }

    // Wait for data to be ready
    document.addEventListener('localization-data-ready', function handler() {
      document.removeEventListener('localization-data-ready', handler);
      resolve(window.localizationData);
    });
  });
}

/**
 * Change country by triggering localization controller
 * @param {string} countryCode - ISO country code to switch to
 */
function changeCountry(countryCode) {
  const localizationController = document.querySelector('localization-controller');

  if (localizationController && typeof localizationController.changeCountry === 'function') {
    localizationController.changeCountry(countryCode);
  } else {
    console.warn('Localization controller not found or changeCountry method not available');
  }
}

// Export functions to global scope
window.getUserLocation = getUserLocation;
window.getCountryCode = getCountryCode;
window.getLocalizationData = getLocalizationData;
window.changeCountry = changeCountry;
